import { combineReducers } from 'redux';

import { setResponse, setResponseExtra } from 'Core/actions/response.ts';
import { createReducersFromShorthands } from 'Core/reducers/common.ts';
import { FacetCollection, SearchItemDataCollection } from 'Models/index.ts';
import fitmentSearchConfig from 'Models/uiConfig/fitmentSearchConfig.js';
import uiConfig from 'Models/uiConfig/uiConfig.js';

const { customFacetValueFilter: customFilter } = uiConfig;

const normalStateMap = {
  Corrected: 'corrected',
  Phonetic: 'corrected',
  Nothing: 'nothing',
  Normal: 'normal',
  Partial: 'partial',
};

const initStates = {
  error: null,
  facets: new FacetCollection(),
  items: new SearchItemDataCollection(),
  messages: [],
  originalQuery: '',
  query: '',
  state: '',
  totalHits: -1,
  extra: null,
};

export default combineReducers(
  createReducersFromShorthands(
    {
      error: { [setResponse.type]: getFromPayload('error') },
      facets: {
        [setResponse.type]: (_state, { payload: { Facets }, meta: { preselection } }) =>
          prepareResponseFacets(
            Facets,
            preselection,
            (f) => !fitmentSearchConfig.fields.includes(f.FieldName),
          ),
      },
      items: {
        [setResponse.type]: (state, { payload: { Items }, meta: { appendItems } }) => {
          const newItems = new SearchItemDataCollection(Items, 'search');
          return appendItems ? state.concat(newItems) : newItems;
        },
      },
      messages: { [setResponse.type]: getFromPayload('Messages', []) },
      originalQuery: { [setResponse.type]: getFromPayload('OriginalQuery') },
      query: { [setResponse.type]: getFromPayload('Query') },
      state: {
        [setResponse.type]: (_state, { payload: { State } }) => normalStateMap[State.split(',')[0]] || State,
      },
      totalHits: { [setResponse.type]: getFromPayload('TotalHits') },
      extra: {
        [setResponse.type]: getFromPayload('Extra'),
        [setResponseExtra.type]: (_state, { payload }) => payload,
      },
    },
    initStates,
  ),
);

function getFromPayload(key, defaultValue = null) {
  return (_state, { payload }) => payload[key] ?? defaultValue;
}

export function prepareResponseFacets(facets, preselection, filter) {
  /* 
    defaultFilter: Don't display preselected facets without the treeLevel.
    Example: the brand PLP will have the 'Brands' preselected facet. There will be only one selected value
    in the response, so don't display this facet with only one selected value which can't be unselected.
  */
  const preselectedFields = preselection.map((v) => v.field);
  const defaultFilter = (v) => !(v.treeLevel === null && preselectedFields.includes(v.field));
  const facetValueFilter = customFilter
    ? (v) => customFilter(v, { filter: defaultFilter, preselection })
    : defaultFilter;

  // preselected using 'getLocalPreselection' functions in platforms and config.js files
  const preselectedTreeLevelMap = Object.fromEntries(
    preselection.filter((v) => typeof v.treeLevel === 'number').map((v) => [v.field, v.treeLevel + 1]),
  );

  const responseFacets = filter ? facets?.filter((f) => filter(f)) : facets;

  return new FacetCollection(responseFacets, facetValueFilter, preselectedTreeLevelMap);
}
